import React, { FC } from 'react';

import { Typography } from '@mui/material';
import { Box } from '@mui/system';

import { ReactComponent as MenuIcon } from '@pharmaplan/common/assets/icons/menuIcon.svg';
import {
  convertToDecimals,
  getHourlyLabel,
  IPharmacyEventsData,
  pharmacyActions,
  PharmacyLegendVariant,
  toCurrencyFormat,
  userPreferredTimeFormat,
} from '@pharmaplan/common';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';

import HourlyRateChipLabel from '../../../../common/AdminHourlyRateFooter/HourlyRateChipLabel';
import useSummaryContent from '../../../../../hooks/SummaryContent/useSummaryContent';
import AdminHourlyRateFooter from '../../../../common/AdminHourlyRateFooter';
import { getTimeWithDiff } from '../../../../Modals/WorkshiftDetails/utils';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import { getDayMonthDateYear } from '../../../../Reports/helpers';
import useSelfService from '../../../../../hooks/useSelfService';
import CustomMenuList from '../../../../common/CustomMenuList';
import PharmacistBar from '../../../../common/PharmacistBar';
import { ChipType } from '../../../../../helpers/Constants';
import useGetItems from '../../../../../hooks/useGetItems';
import WithSkeleton from '../../../../common/WithSkeleton';
import BookingRequests from '../../BookingRequests';
import strings from '../../../../../localization';
import CustomChip from '../../../../common/CustomChip';

import ShiftIdentifier from './ShiftIdentifier';
import styles from './styles';

const { booked, requested } = PharmacyLegendVariant;
const successAction = pharmacyActions.getPharmacyEventsByDate;

interface ISummaryContent {
  event: IPharmacyEventsData;
  preventInteraction?: boolean;
  type: PharmacyLegendVariant;
}

export const toWeekdayWeekendFormat = (
  weekday: number | string | undefined,
  weekend: number | string | undefined,
) => {
  if (!weekday) return `${toCurrencyFormat(weekend)}${strings.perHour}`;
  if (!weekend) return `${toCurrencyFormat(weekday)}${strings.perHour}`;

  return `${toCurrencyFormat(weekday)} / ${toCurrencyFormat(weekend)}`;
};

const SummaryContent: FC<ISummaryContent> = ({
  event,
  type,
  preventInteraction,
}) => {
  const { isSelfService } = useSelfService();
  const { getPharmacyProfessionalImage } = useGetItems();

  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const {
    startDate = '',
    endDate = '',
    professionalName,
    replacementType,
    pharmacistName,
    requestedCount,
    professionalIds,
    totalShiftCount,
    bookedShiftCount,
    longTermShiftContractId,
    contractEndDate,
    weekDayHourlyRate,
    weekendHourlyRate,
    contractStartDate,
  } = event ?? {};

  const name = isSelfService ? professionalName : pharmacistName;
  const getRequestedImages = professionalIds?.map(
    (item) =>
      getPharmacyProfessionalImage(item) ?? '',
  );

  const hours = getTimeWithDiff(startDate, endDate, timeFormat);

  const dateLabel = `${getDayMonthDateYear(startDate)}`;
  const isBooked = type === booked;
  const isRequested = type === requested && isSelfService;

  const {
    goToRequestedList,
    goToUpdate,
    confirmMultipleDelete,
    confirmRejection,
    openBookedPharmacy,
  } = useSummaryContent({
    event,
    type,
    preventInteraction,
    longTermShiftContractId,
  });

  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: 'text',
  })(Typography);

  const label = toWeekdayWeekendFormat(weekDayHourlyRate, weekendHourlyRate);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <Box sx={styles.shiftIdentifierContainer}>
          <ShiftIdentifier
            totalShiftCount={totalShiftCount}
            bookedShiftCount={bookedShiftCount}
            isLongTerm={!!longTermShiftContractId}
            contractStartDate={contractStartDate}
            contractEndDate={contractEndDate}
            successAction={pharmacyActions.getPharmacyEventsByDate}
          />
          <div>
            {!longTermShiftContractId && (
              <>
                <Box sx={styles.content}>
                  <SkeletonTypography sx={styles.label}>
                    {dateLabel}
                  </SkeletonTypography>
                </Box>

                <SkeletonTypography sx={styles.label}>
                  {hours}
                </SkeletonTypography>
              </>
            )}
          </div>
        </Box>

        {!isBooked && (
          <CustomMenuList
            margin={10}
            disableFixedDropdown
            customMenuTextStyle={styles.menuText}
            customIconButtonStyle={styles.button}
            items={[
              {
                key: '2',
                label: strings.edit,
                onClick: goToUpdate,
              },
              {
                key: '3',
                label: strings.delete,
                onClick: longTermShiftContractId
                  ? confirmMultipleDelete
                  : confirmRejection,
              },
            ]}
          >
            <MenuIcon height={24} width={24} />
          </CustomMenuList>
        )}
      </Box>

      <AdminHourlyRateFooter
        replacementType={replacementType}
        displayReplacement
        successAction={pharmacyActions.getPharmacyEventsByDate}
        customHourlyLabel={strings.hourlyRate}
        pharmacistHourlyRate={event.paidHourlyRate ?? 0}
        customPharmacistChip={
          longTermShiftContractId ? (
            <HourlyRateChipLabel
              customContainerStyle={styles.chipContainer}
              label={`${strings.hourlyRate} ${getHourlyLabel(
                weekDayHourlyRate,
                weekendHourlyRate,
              )}`}
              customChip={
                <CustomChip label={label} type={ChipType.hourlyRate} />
              }
              type={ChipType.hourlyRate}
              successAction={successAction}
            />
          ) : null
        }
      />

      {isRequested && (
        <BookingRequests
          customContainerStyle={styles.requestContainer}
          onClick={goToRequestedList}
          profileImage={getRequestedImages ?? []}
          requestedCount={requestedCount}
          hideTitle
        />
      )}

      {isBooked && (
        <PharmacistBar
          customStyle={styles.barContainer}
          hideHourly
          image={
            getPharmacyProfessionalImage(
              event?.[idStore.id as keyof typeof event] as string,
            ) ?? ' '
          }
          name={name ?? ''}
          successAction={pharmacyActions.getPharmacyEventsByDate}
          imageSuccessAction={pharmacyActions.getProfessionalPictures}
          hourlyRate={
            strings.formatString(
              strings.hourlyRateSuffix,
              convertToDecimals(100) as number,
            ) as string
          }
          type={booked}
          arrowClick={openBookedPharmacy}
        />
      )}
    </Box>
  );
};

export default SummaryContent;
